/**
 * Created by robert.lavoie on 9/8/17.
 */

import {
  Directive,
  ElementRef,
  Inject,
  Input,
  HostListener,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BrowserService } from '../services';
import { Subscription } from 'rxjs';
import { BrowserMode } from '../services/browser.service';

@Directive({
  selector: '[StickyScroll]'
})
export class StickyScrollDirective implements OnDestroy, OnInit {

  @Input() private desktopOnly: boolean = false;
  @Input() private mobileOnly: boolean = false;
  @Input() private top: boolean = true;

  private bottomStick: string;
  private browserMode: string;
  private browserModeSubscription: Subscription;
  private originalY: number;
  private topStick: string;

  constructor(
    private browserService: BrowserService,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit() {
    // Return out if we are not on a browser
    if (!isPlatformBrowser(this.platformId)) return;

    // Init our class strings
    this.topStick = 'sticky-top';
    this.bottomStick = 'sticky-bottom';

    // Change them if mobile only is requested
    if (this.mobileOnly) {
      this.topStick = 'sticky-top-mobile';
      this.bottomStick = 'sticky-bottom-mobile';
    }

    // Change them if desktop only is requested
    if (this.desktopOnly) {
      this.topStick = 'sticky-top-desktop';
      this.bottomStick = 'sticky-bottom-desktop';
    }

    // Browser mode subscription
    this.browserModeSubscription = this.browserService.getMode().subscribe(data => this.browserMode = data);
  }

  // Reset everything on destroy
  ngOnDestroy() {
    if (this.browserModeSubscription) this.browserModeSubscription.unsubscribe();
    this.resetStick();
  }

  // Only binds to window if window exists
  @HostListener('window:scroll', ['$event']) onScroll($event) {
    // Return out if we are not on a browser, or on the wrong browser
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.mobileOnly && this.browserMode == BrowserMode.Desktop) return;
    if (this.desktopOnly && this.browserMode !== BrowserMode.Desktop) return;

    // Return if we can not scroll, as nothing needs to stick then
    if (document.body.scrollHeight <= window.innerHeight) {
      this.resetStick();
      return;
    }

    // Calculate the distance from the top of the page, even if negative
    let distanceFromTop = this.elementRef.nativeElement.getBoundingClientRect().top + window.pageYOffset;

    // If we have an original y value set, use it instead as we are now "stuck"
    if (this.originalY) distanceFromTop = this.originalY;

    // Handle bottom of page calculations to make bottom sticky smooth
    const bottomOffset = window.innerHeight - this.elementRef.nativeElement.offsetHeight;
    const distanceFromBottom = distanceFromTop - bottomOffset;

    // Conditions for top and bottom stickies, if neither then reset
    if (this.top && window.pageYOffset >= distanceFromTop) this.stickElement(this.topStick, true, distanceFromTop);
    else if (!this.top && window.pageYOffset <= distanceFromBottom) this.stickElement(this.bottomStick, false, distanceFromTop);
    else this.resetStick();
  }

   /**
   * Helper function to stick the element and track original position
   * @param className {string} - string of the classname to be added
   * @param addPageHeight {boolean} - whether or not to add placeholder space on mobile
   * @param distanceFromTop {number} - distance to save from the element to the top of the document
   */
  stickElement(className: string, addPageHeight: boolean = false, distanceFromTop: number) {
    if (this.originalY == null) this.originalY = distanceFromTop;
    if (addPageHeight) this.addPageHeight();
    this.renderer.addClass(this.elementRef.nativeElement, className);
  }

  /**
   * Adds page height for smooth sticky scrolling only when mobile
   */
  addPageHeight() {
    window.document.body.style.marginTop = this.elementRef.nativeElement.offsetHeight + 'px';
  }

  /**
   * Resets all logic
   */
  resetStick() {
    // Return out if we are not on a browser
    if (!isPlatformBrowser(this.platformId)) return;

    // Reset the logic
    this.renderer.removeClass(this.elementRef.nativeElement, this.topStick);
    this.renderer.removeClass(this.elementRef.nativeElement, this.bottomStick);
    this.originalY = null;

    // Remove margin if we have a top stick element
    if (this.top) window.document.body.style.marginTop = '';
  }
}
